// src/public/tests/NumericalReasoningTest.js
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  LinearProgress,
  Alert,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

// Preguntas de Razonamiento Numérico (42 preguntas)
const numericalReasoningQuestions = [
  { id: 1, series: [2, 4, 6, 8, 10, 12], answer1: 14, answer2: 16 },
  { id: 2, series: [17, 16, 15, 14, 13, 12], answer1: 11, answer2: 10 },
  { id: 3, series: [3, 3, 4, 4, 5, 5], answer1: 6, answer2: 6 },
  { id: 4, series: [21, 23, 25, 27, 29, 31], answer1: 33, answer2: 35 },
  { id: 5, series: [8, 8, 10, 10, 12, 12], answer1: 14, answer2: 14 },
  { id: 6, series: [3, 2, 4, 2, 5, 2], answer1: 6, answer2: 2 },
  { id: 7, series: [7, 11, 15, 19, 23, 27], answer1: 31, answer2: 35 },
  { id: 8, series: [21, 21, 16, 16, 11, 11], answer1: 6, answer2: 6 },
  { id: 9, series: [4, 3, 5, 3, 6, 3], answer1: 7, answer2: 3 },
  { id: 10, series: [14, 19, 24, 29, 34, 39], answer1: 44, answer2: 49 },
  { id: 11, series: [40, 36, 32, 28, 24, 20], answer1: 16, answer2: 12 },
  { id: 12, series: [3, 10, 17, 24, 31, 38], answer1: 45, answer2: 52 },
  { id: 13, series: [2, 5, 8, 11, 14, 17], answer1: 20, answer2: 23 },
  { id: 14, series: [94, 85, 76, 67, 58, 49], answer1: 40, answer2: 31 },
  { id: 15, series: [2, 10, 18, 26, 34, 42], answer1: 50, answer2: 58 },
  { id: 16, series: [9, 8, 8, 7, 7, 6], answer1: 6, answer2: 5 },
  { id: 17, series: [95, 84, 73, 62, 51, 40], answer1: 29, answer2: 18 },
  { id: 18, series: [21, 7, 17, 7, 13, 7], answer1: 9, answer2: 7 },
  { id: 19, series: [10, 1, 9, 2, 8, 3], answer1: 7, answer2: 4 },
  { id: 20, series: [2, 6, 6, 10, 10, 14], answer1: 14, answer2: 18 },
  { id: 21, series: [3, 5, 4, 6, 5, 7], answer1: 7, answer2: 6 },
  { id: 22, series: [2, 3, 3, 3, 4, 3], answer1: 5, answer2: 3 },
  { id: 23, series: [7, 6, 8, 7, 9, 8], answer1: 10, answer2: 9 },
  { id: 24, series: [2, 4, 3, 5, 4, 6], answer1: 5, answer2: 7 },
  { id: 25, series: [2, 3, 4, 3, 8, 3], answer1: 16, answer2: 3 },
  { id: 26, series: [1, 2, 4, 7, 11, 16], answer1: 22, answer2: 29 },
  { id: 27, series: [5, 5, 10, 5, 15, 5], answer1: 20, answer2: 5 },
  { id: 28, series: [6, 1, 9, 4, 12, 7], answer1: 15, answer2: 10 },
  { id: 29, series: [36, 35, 33, 30, 26, 21], answer1: 15, answer2: 8 },
  { id: 30, series: [45, 36, 28, 21, 15, 10], answer1: 6, answer2: 3 },
  { id: 31, series: [3, 6, 4, 7, 5, 8], answer1: 6, answer2: 9 },
  { id: 32, series: [9, 8, 10, 7, 11, 6], answer1: 12, answer2: 5 },
  { id: 33, series: [8, 3, 13, 3, 18, 3], answer1: 23, answer2: 3 },
  { id: 34, series: [2, 4, 8, 16, 32, 64], answer1: 128, answer2: 256 },
  { id: 35, series: [3, 9, 15, 21, 27, 33], answer1: 39, answer2: 45 },
  { id: 36, series: [13, 18, 14, 15, 15, 12], answer1: 16, answer2: 9 },
  { id: 37, series: [3, 5, 8, 12, 17, 23], answer1: 30, answer2: 38 },
  { id: 38, series: [7, 8, 15, 23, 38, 61], answer1: 99, answer2: 160 },
  { id: 39, series: [2, 5, 11, 23, 47, 95], answer1: 191, answer2: 383 },
  { id: 40, series: [5, 10, 14, 28, 32, 64], answer1: 68, answer2: 136 },
  { id: 41, series: [1, 2, 5, 12, 27, 58], answer1: 121, answer2: 248 },
  { id: 42, series: [3, 5, 9, 17, 33, 65], answer1: 129, answer2: 257 },
];

const QUESTIONS_PER_PAGE = 4;

const NumericalReasoningTest = ({
  responses = {},
  onAnswerChange,
  currentQuestion,
  onQuestionChange,
  onComplete,
  saving,
  timeRemaining = null
}) => {
  const { t } = useTranslation();
  const [pageAnswers, setPageAnswers] = useState({});
  const [error, setError] = useState('');

  const totalQuestions = numericalReasoningQuestions.length;
  const totalPages = Math.ceil(totalQuestions / QUESTIONS_PER_PAGE);
  const currentPage = Math.floor(currentQuestion / QUESTIONS_PER_PAGE);
  const progress = ((currentPage + 1) / totalPages) * 100;

  // Calcular índices de preguntas para la página actual
  const startIndex = currentPage * QUESTIONS_PER_PAGE;
  const endIndex = Math.min(startIndex + QUESTIONS_PER_PAGE, totalQuestions);
  const questionsInPage = numericalReasoningQuestions.slice(startIndex, endIndex);

  // Cargar respuestas guardadas al cambiar de página
  useEffect(() => {
    const loadedAnswers = {};
    questionsInPage.forEach(question => {
      const savedAnswers = responses[question.id];
      if (savedAnswers) {
        loadedAnswers[question.id] = savedAnswers;
      } else {
        loadedAnswers[question.id] = ['', ''];
      }
    });
    setPageAnswers(loadedAnswers);
    setError('');
  }, [currentPage]);

  // Actualizar una respuesta específica
  const handleAnswerChange = (questionId, index, value) => {
    // Solo permitir números y signo negativo
    if (value !== '' && !/^-?\d*$/.test(value)) {
      return;
    }

    const currentAnswers = pageAnswers[questionId] || ['', ''];
    const newAnswers = [...currentAnswers];
    newAnswers[index] = value;

    setPageAnswers(prev => ({
      ...prev,
      [questionId]: newAnswers
    }));

    // Subir la respuesta al padre en cada cambio: si el tiempo expira en esta
    // página, el auto-guardado del timeout incluye lo respondido hasta ahora
    // (antes solo se guardaba al navegar con Siguiente/Anterior/Finalizar).
    onAnswerChange(questionId, newAnswers);

    setError('');
  };

  // Construir el objeto completo de respuestas incluyendo las actuales
  const getAllResponses = () => {
    const allResponses = { ...responses };

    // Agregar/actualizar las respuestas de la página actual
    questionsInPage.forEach(question => {
      const answers = pageAnswers[question.id] || ['', ''];
      allResponses[question.id] = answers;
    });

    return allResponses;
  };

  // Navegar a la siguiente página
  const handleNext = () => {
    // Construir todas las respuestas incluyendo las de esta página
    const allResponses = getAllResponses();

    if (currentPage < totalPages - 1) {
      // Ir a la primera pregunta de la siguiente página
      const nextPageFirstQuestion = (currentPage + 1) * QUESTIONS_PER_PAGE;
      // Pasar las respuestas directamente
      onQuestionChange(nextPageFirstQuestion, allResponses);
    } else {
      // Última página - primero actualizar las respuestas en el padre, luego finalizar
      onComplete(allResponses);
    }
  };

  // Navegar a la página anterior
  const handlePrevious = () => {
    if (currentPage > 0) {
      // Construir todas las respuestas incluyendo las de esta página
      const allResponses = getAllResponses();

      // Ir a la primera pregunta de la página anterior
      const prevPageFirstQuestion = (currentPage - 1) * QUESTIONS_PER_PAGE;
      // Pasar las respuestas directamente
      onQuestionChange(prevPageFirstQuestion, allResponses);
    }
  };

  // Deshabilitar botones si el tiempo expiró
  const isTimeExpired = timeRemaining !== null && timeRemaining <= 0;

  return (
    <Paper elevation={3} sx={{ p: 4 }}>
      {/* Header con progreso */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('reasoningTests.pageProgress', {
            current: currentPage + 1,
            total: totalPages
          })} | {t('reasoningTests.questionsInPage', {
            start: startIndex + 1,
            end: endIndex,
            total: totalQuestions
          })}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>

      {/* Mensaje de tiempo expirado */}
      {isTimeExpired && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('reasoningTests.timeExpired')}
        </Alert>
      )}

      {/* Preguntas de la página actual */}
      <Box sx={{ mb: 4 }}>
        {questionsInPage.map((question, idx) => {
          const answers = pageAnswers[question.id] || ['', ''];
          return (
            <Box key={question.id} sx={{ mb: idx < questionsInPage.length - 1 ? 4 : 0 }}>
              {/* Número de pregunta */}
              <Typography variant="h6" color="primary" gutterBottom>
                {t('reasoningTests.questionNumber', { number: startIndex + idx + 1 })}
              </Typography>

              {/* Secuencia de números Y campos de respuesta en la MISMA LÍNEA */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
                flexWrap: 'wrap',
                justifyContent: 'flex-start'
              }}>
                {question.series.map((num, numIdx) => (
                  <Box
                    key={numIdx}
                    sx={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: 'text.primary',
                      minWidth: '50px',
                      textAlign: 'center',
                    }}
                  >
                    {num}
                  </Box>
                ))}

                {/* Campos de respuesta */}
                <TextField
                  value={answers[0]}
                  onChange={(e) => handleAnswerChange(question.id, 0, e.target.value)}
                  variant="outlined"
                  placeholder="?"
                  disabled={isTimeExpired}
                  sx={{
                    width: '100px',
                    '& input': {
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      padding: '10px',
                    },
                  }}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '-?[0-9]*',
                  }}
                />

                <TextField
                  value={answers[1]}
                  onChange={(e) => handleAnswerChange(question.id, 1, e.target.value)}
                  variant="outlined"
                  placeholder="?"
                  disabled={isTimeExpired}
                  sx={{
                    width: '100px',
                    '& input': {
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      padding: '10px',
                    },
                  }}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '-?[0-9]*',
                  }}
                />
              </Box>

              {/* Divider entre preguntas (excepto la última) */}
              {idx < questionsInPage.length - 1 && (
                <Divider sx={{ mt: 3 }} />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Botones de navegación */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={handlePrevious}
          disabled={currentPage === 0 || saving || isTimeExpired}
        >
          {t('reasoningTests.previous')}
        </Button>

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={saving || isTimeExpired}
          size="large"
        >
          {currentPage === totalPages - 1
            ? t('reasoningTests.finish')
            : t('reasoningTests.nextPage')
          }
        </Button>
      </Box>

      {/* Indicador de guardado */}
      {saving && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 2 }}
        >
          {t('reasoningTests.savingProgress')}
        </Typography>
      )}
    </Paper>
  );
};

export default NumericalReasoningTest;